import Swiper from 'swiper';
import { Navigation } from 'swiper/modules';
import { initializeProductComparison } from './product-comparison.js';
import * as loadingMask from './load-mask.js';
import { EVENT_DELETED, EVENT_SAVED, openFavoriteSheet } from './favorite-sheet.js';
import { createFeedbackDialog } from './feedback-dialog-module.js';
import { postAction } from './utils/action-endpoint.js';
import { initTopsellerSlider } from './topseller.js';
import { initEnergyEfficiency } from './widgets/energy-efficiency.js';
import { addCampaignNotificationListeners } from './campaign-notification.js';
import {
  saveZubehoerSelection,
  restoreZubehoerSelection,
  clearCurrentZubehoerStorage,
  clearZubehoerStorage,
} from './zubehoer-storage.js';

// The no-module script asks for the sheet of a combination that is not on stock.
const EVENT_OPEN_UNAVAILABLE = 'favorite:open-unavailable';

// Expose zubehoer storage functions globally so non-module scripts
// (modell-no-module.js, buybox.js) can use them
window.saveZubehoerSelection = saveZubehoerSelection;
window.restoreZubehoerSelection = restoreZubehoerSelection;
window.clearCurrentZubehoerStorage = clearCurrentZubehoerStorage;
window.clearZubehoerStorage = clearZubehoerStorage;

// Registered once at module scope: initializeModellPage() can run again on AJAX
// re-renders, which would stack the document-level delegation listener.
addCampaignNotificationListeners();
addFavoriteListeners();

window.addEventListener('load', function () {
  initializeModellPage();

  window.initializeModellPage = initializeModellPage;
});

/**
 * @param {?string[]} replacedContainerIds Ids of the containers a dynamic response actually replaced,
 *   or null on a full page load, when everything is freshly mounted and needs initialising.
 *   The topseller slider and product detail/comparison initialisers are not idempotent, so they must
 *   not run again over elements that survived a swap that did not touch their container.
 */
function initializeModellPage(replacedContainerIds = null) {
  if (replacedContainerIds === null || replacedContainerIds.includes('js-pds-topseller')) {
    initTopsellerSlider();
  }
  if (replacedContainerIds === null || replacedContainerIds.includes('js-pds-details')) {
    addDetailSwitchEvents();
    initializeProductComparison();
  }
  addNewBatteryEvents();
  initEnergyEfficiency();
  scrollToSelectedOptions();
  initSwiperPictureSlider();
  addEquipmentButtonEvent(document);
}

/**
 * Ensures that selected options in scrollable filter containers are visible when the page loads
 */
function scrollToSelectedOptions() {
  // Only apply on mobile screens (screens smaller than desktop breakpoint)
  if (window.innerWidth > 768) {
    return;
  }

  document.querySelectorAll('.js_configurator_grid').forEach((container) => {
    const activeButton = container.querySelector('.js_configurator_option.active');
    if (!activeButton) {
      return;
    }

    const containerWidth = container.offsetWidth;
    const buttonLeft = activeButton.offsetLeft;
    const buttonWidth = activeButton.offsetWidth;

    const targetScrollLeft = Math.max(0, buttonLeft - containerWidth / 2 + buttonWidth / 2);

    // Only scroll if the difference is significant (more than 1px) to avoid unnecessary layout shifts/jumps
    if (Math.abs(container.scrollLeft - targetScrollLeft) > 1) {
      container.scrollLeft = targetScrollLeft;
    }
  });
}

/**
 * On mobile initially only two items are shown
 * When clicking the button all items are shown and this button is hidden
 *
 * @param {HTMLElement|Document} area
 */
async function addEquipmentButtonEvent(area) {
  const showAllEquipmentButtons = area.querySelectorAll('.js_show_all_equipment');

  showAllEquipmentButtons.forEach(function (showAllEquipmentButton) {
    const parent = showAllEquipmentButton.parentElement;

    showAllEquipmentButton.addEventListener('click', function () {
      parent.querySelectorAll('.js_buybox_equipment_item.hidden_on_mobile').forEach(function (el) {
        el.classList.remove('hidden_on_mobile');
      });
      showAllEquipmentButton.style.display = 'none';
    });
  });
}

/**
 * Opens the sheet starting from the condition and VAT filter shown.
 *
 * @param {number|null} articleTypeId null for the article type shown
 * @param {number} okClass
 * @param {number} vatShown 1 while only items with the VAT shown are listed
 * @param {boolean} [isUnavailable] the picked combination is not on stock
 */
async function openModelFavoriteSheet(articleTypeId, okClass, vatShown, isUnavailable = false) {
  const heart = document.querySelector('.js_favorite_btn');
  if (!heart) {
    return;
  }

  const yes = heart.dataset.valueYes;
  const form = await openFavoriteSheet(
    heart,
    new URLSearchParams({
      articleTypeId: articleTypeId ?? heart.dataset.articleTypeId,
      okClass,
      vatShown: Number(vatShown) === 1 ? yes : '',
      unavailable: isUnavailable ? yes : '',
    }),
  );
  form?.addEventListener(EVENT_SAVED, (event) =>
    showFavoriteState(event.detail.favoriteId, 'Erfolgreich gespeichert!'),
  );
  form?.addEventListener(EVENT_DELETED, () => showFavoriteState(null, 'Erfolgreich entfernt!'));
}

/**
 * @param {number|null} favoriteId
 * @param {string} buyboxText
 */
function showFavoriteState(favoriteId, buyboxText) {
  const isFavorite = favoriteId !== null;
  const heartButton = document.querySelector('.js_favorite_btn');
  if (heartButton instanceof HTMLElement) {
    heartButton.dataset.isFavorite = String(isFavorite);
  }
  const heartIcon = heartButton?.querySelector('.icon');
  heartIcon?.classList.toggle('icon_heartfull_red', isFavorite);
  heartIcon?.classList.toggle('icon_heart', !isFavorite);

  const favoriteText = document.querySelector('.js_favorite_text');
  if (!favoriteText) {
    return;
  }

  favoriteText.textContent = buyboxText;
  document.querySelectorAll('.js_add_favorite').forEach((button) => {
    button.style.display = isFavorite ? 'none' : 'inline-flex';
  });
  document.querySelectorAll('.js_remove_favorite').forEach((button) => {
    button.style.display = isFavorite ? 'inline-flex' : 'none';
    button.dataset.vm = isFavorite ? String(favoriteId) : '';
  });
}

/**
 * @param {string} favoriteId
 */
async function removeFavorite(favoriteId) {
  const heart = document.querySelector('.js_favorite_btn');
  loadingMask.show();
  try {
    await postAction(heart.dataset.deleteUrl, new URLSearchParams({ id: favoriteId }));
    showFavoriteState(null, 'Erfolgreich entfernt!');
  } catch (failure) {
    createFeedbackDialog('feedback_error', failure.response?.message ?? heart.dataset.messageError);
  } finally {
    loadingMask.hide();
  }
}

// Registered once at module scope: the heart and the buybox are re-rendered with the dynamic sections.
function addFavoriteListeners() {
  document.addEventListener('click', (event) => {
    const removeButton = event.target.closest('.js_remove_favorite');
    if (removeButton) {
      removeFavorite(removeButton.dataset.vm);
    } else if (event.target.closest('.js_favorite_btn, .js_add_favorite')) {
      openModelFavoriteSheet(null, window.currOk, window.mwst);
    }
  });

  document.addEventListener(EVENT_OPEN_UNAVAILABLE, (event) => {
    const { articleTypeId, okClass, vatShown } = event.detail;
    openModelFavoriteSheet(articleTypeId, okClass, vatShown, true);
  });
}

async function addNewBatteryEvents() {
  const url = new URL(location.href);

  const addBatteryButton = document.querySelector('.js_add_new_battery');
  if (addBatteryButton) {
    addBatteryButton.addEventListener('click', function () {
      loadingMask.show();
      url.searchParams.set('new-battery', '1');
      historyManager.load(url.search);
    });
  }

  const removeBatteryButton = document.querySelector('.js_remove_new_battery');
  if (removeBatteryButton) {
    removeBatteryButton.addEventListener('click', function () {
      loadingMask.show();
      url.searchParams.delete('new-battery');
      historyManager.load(url.search);
    });
  }
}

async function addDetailSwitchEvents() {
  const switchButtons = document.querySelectorAll('.js_product_details_switch_button');

  if (switchButtons.length > 0) {
    switchButtons[0].classList.add('active');
  }

  switchButtons.forEach((switchButton) => {
    switchButton.addEventListener('click', function () {
      switchButtons.forEach(function (button) {
        button.classList.remove('active');
      });
      switchButton.classList.add('active');
      const contentElement = document.getElementById(switchButton.dataset.content);
      document.querySelectorAll('.js_product_details_content').forEach(function (content) {
        content.classList.remove('active');
      });
      contentElement.classList.add('active');
      document.querySelector('.js_product_details_scroll_anchor').scrollIntoView({ behavior: 'smooth' });
    });
  });
}

function initSwiperPictureSlider() {
  const slider = document.querySelector('.js_picture_slider');
  if (!slider) {
    return;
  }
  const swiper = new Swiper(slider, {
    modules: [Navigation],
    slidesPerView: 1,
    spaceBetween: 16,
    loop: true,
    navigation: {
      nextEl: slider.querySelector('.swiper-button-next'),
      prevEl: slider.querySelector('.swiper-button-prev'),
      addIcons: false,
    },
  });

  document.querySelectorAll('.js_image_thumbnail').forEach(function (imageThumbnail) {
    imageThumbnail.addEventListener('click', function () {
      const index = Number(imageThumbnail.dataset.index);
      // slideTo() targets internal indices that include loop clones — slideToLoop()
      // is the correct API when loop: true is enabled.
      if (typeof swiper.slideToLoop === 'function') {
        swiper.slideToLoop(index);
      } else {
        swiper.slideTo(index);
      }
    });
  });
}
